import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Search, Trash2, Edit, FileText, CheckCircle, Printer, MessageCircle, Eye } from "lucide-react";
import { printPO, whatsappPO } from "@/utils/printPO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();
  const { isFullAdmin, isManager, token } = useAuth();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [formData, setFormData] = useState({
    supplier_name: "",
    supplier_contact: "",
    generated_by: "",
    items: [{ name: "", quantity: 1, price: 0, total: 0 }],
    status: "Pending Approval",
    notes: "",
    attachment: "",
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: statusFilter === "all" ? undefined : statusFilter }
      });
      setOrders(res.data.data);
    } catch (err) {
      toast({ title: "Error", description: "Failed to load purchase orders", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === "quantity" || field === "price") {
      newItems[index].total = Number(newItems[index].quantity) * Number(newItems[index].price);
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { name: "", quantity: 1, price: 0, total: 0 }] });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => formData.items.reduce((acc, item) => acc + (Number(item.total) || 0), 0);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, attachment: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, total_amount: calculateTotal() };
    try {
      if (editingPo) {
        await axios.put(`${API_URL}/purchase-orders/${editingPo.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Purchase order updated" });
      } else {
        await axios.post(`${API_URL}/purchase-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Purchase order created" });
      }
      setIsDialogOpen(false);
      fetchOrders();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Action failed", variant: "destructive" });
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (newStatus === "Received") {
      if (!window.confirm("Are you sure you want to mark this order as received?")) return;
    }
    try {
      await axios.put(`${API_URL}/purchase-orders/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `Order marked as ${newStatus}` });
      fetchOrders();
    } catch (err) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Are you sure you want to delete this purchase order?")) return;
    try {
      await axios.delete(`${API_URL}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Order deleted" });
      fetchOrders();
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete order", variant: "destructive" });
    }
  };

  const openNew = () => {
    setEditingPo(null);
    setFormData({
      supplier_name: "", supplier_contact: "", generated_by: "", items: [{ name: "", quantity: 1, price: 0, total: 0 }], status: "Pending Approval", notes: "", attachment: ""
    });
    setIsDialogOpen(true);
  };

  const openEdit = (po) => {
    setEditingPo(po);
    setFormData({
      supplier_name: po.supplier_name,
      supplier_contact: po.supplier_contact || "",
      generated_by: po.generated_by || "",
      items: po.items.length > 0 ? po.items : [{ name: "", quantity: 1, price: 0, total: 0 }],
      status: po.status,
      notes: po.notes || "",
      attachment: po.attachment || "",
    });
    setIsDialogOpen(true);
  };

  const filteredOrders = orders.filter(o => 
    o.supplier_name.toLowerCase().includes(search.toLowerCase()) || 
    (o.po_number && o.po_number.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-sm text-gray-500">Manage supplier orders and inventory arrivals.</p>
        </div>
        <Button onClick={openNew}><Plus className="w-4 h-4 mr-2" /> Create PO</Button>
      </div>

      <div className="flex gap-4 mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Search by supplier or PO number..." 
            className="pl-9" 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
        </div>
        <div className="w-[200px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Pending Approval">Pending Approval</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Received">Received</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO Number</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total Amount</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading purchase orders...</TableCell></TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No purchase orders found.</TableCell></TableRow>
            ) : (
              filteredOrders.map(po => (
                <TableRow key={po.id}>
                  <TableCell className="font-medium">{po.po_number}</TableCell>
                  <TableCell>{new Date(po.order_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="font-medium text-gray-900">{po.supplier_name}</div>
                    <div className="text-xs text-gray-500">{po.supplier_contact}</div>
                    {po.generated_by && <div className="text-[10px] text-gray-400 mt-1">Req by: {po.generated_by}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={po.status === "Received" ? "default" : po.status === "Cancelled" ? "destructive" : po.status === "Pending Approval" ? "outline" : "secondary"}>
                      {po.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-semibold">₹{po.total_amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-right space-x-2">

                    {po.status === "Pending" && (
                      <Button variant="outline" size="sm" onClick={() => updateStatus(po.id, "Received")} title="Mark Received">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </Button>
                    )}
                    {po.attachment && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${po.attachment}`, '_blank')} title="View Attachment">
                        <Eye className="w-4 h-4 text-purple-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => printPO(po)} title="Print / Download PDF">
                      <Printer className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => whatsappPO(po)} title="Send on WhatsApp">
                      <MessageCircle className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(po)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteOrder(po.id)}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPo ? "Edit Purchase Order" : "Create Purchase Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Supplier Name *</label>
                <Input required value={formData.supplier_name} onChange={e => setFormData({...formData, supplier_name: e.target.value})} placeholder="Acme Corp" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Supplier Contact</label>
                <Input value={formData.supplier_contact} onChange={e => setFormData({...formData, supplier_contact: e.target.value})} placeholder="Phone or Email" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Generated By *</label>
                <Input required value={formData.generated_by} onChange={e => setFormData({...formData, generated_by: e.target.value})} placeholder="Your Name" />
              </div>
            </div>

            <div className="border rounded-md p-4 bg-gray-50/50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-sm">Order Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
              </div>
              
              <div className="space-y-3">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="flex flex-col md:flex-row gap-2 md:items-center bg-white p-3 md:p-0 rounded border md:border-none">
                    <Input required placeholder="Item Name" className="w-full md:flex-1" value={item.name} onChange={e => handleItemChange(idx, "name", e.target.value)} />
                    <div className="flex gap-2 items-center w-full md:w-auto">
                      <Input required type="number" min="1" placeholder="Qty" className="flex-1 md:w-24" value={item.quantity} onChange={e => handleItemChange(idx, "quantity", e.target.value)} />
                      <Input required type="number" min="0" step="0.01" placeholder="Price" className="flex-1 md:w-32" value={item.price} onChange={e => handleItemChange(idx, "price", e.target.value)} />
                      <div className="w-24 text-right font-medium text-sm">₹{item.total.toLocaleString()}</div>
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 px-2" onClick={() => removeItem(idx)} disabled={formData.items.length === 1}><Trash2 className="w-4 h-4"/></Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <span className="font-semibold">Total Amount:</span>
                <span className="text-lg font-bold">₹{calculateTotal().toLocaleString()}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes / Instructions</label>
                <Input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Delivery instructions..." />
              </div>
              {editingPo && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending Approval">Pending Approval</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Received">Received</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Attachment (Image/Receipt)</label>
              <div className="flex items-center gap-4">
                <Input type="file" accept="image/*" capture="environment" onChange={handleImageUpload} className="w-full md:w-1/2" />
                {formData.attachment && (
                  <div className="h-16 w-16 border rounded overflow-hidden">
                    <img src={formData.attachment.startsWith('data:') ? formData.attachment : `${process.env.REACT_APP_BACKEND_URL}${formData.attachment}`} alt="Attachment preview" className="object-cover w-full h-full" />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingPo ? "Update Order" : "Create Order"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
